import React, { useCallback } from "react";
import styled from "styled-components";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { FOCUS_BORDER } from "../../config/focusStyles";
import { useInputMode } from "../../utils/hooks/useInputMode";

/**
 * ViewMoreCard - A focusable "View More" card for content row sliders.
 * 
 * Placed at the end of content rows when items > 12.
 * Styled with non-blur gradient glassmorphism effect for legacy TV compatibility.
 * 
 * @param {Object} props
 * @param {Function} props.onFocus - Called when card receives focus
 * @param {string} props.actionLink - Navigation link in format "type;target"
 * @param {Function} props.onMoreTap - Optional callback override for tap action
 * @param {number} props.width - Card width (default 250)
 * @param {number} props.height - Card height (default 350)
 * @param {string} props.shapeType - Shape type for sizing (movie, music, etc.)
 */

// ============================================================================
// STYLED COMPONENTS (MUI-Free for legacy TV compatibility)
// ============================================================================

var CardWrapper = styled.div`
  border-radius: 15px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: ${function (props) {
    return props.$focused
      ? "linear-gradient(145deg, rgba(70, 70, 80, 0.9) 0%, rgba(45, 45, 55, 0.92) 40%, rgba(25, 25, 30, 0.95) 100%)"
      : "linear-gradient(145deg, rgba(55, 55, 65, 0.75) 0%, rgba(35, 35, 42, 0.82) 40%, rgba(20, 20, 25, 0.88) 100%)";
  }};
  box-shadow: ${function (props) {
    return props.$focused
      ? "0 8px 32px rgba(0, 0, 0, 0.5)"
      : "0 4px 16px rgba(0, 0, 0, 0.4)";
  }};
`;

var TopShine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 60%, transparent 100%);
  pointer-events: none;
  z-index: 1;
`;

var SideAccent = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 40%;
  background: linear-gradient(270deg, rgba(120, 120, 130, 0.12) 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
`;

var BottomShadow = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(0deg, rgba(0,0,0,0.4) 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
`;

var ContentContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: table;
  width: 100%;
  height: 100%;
  z-index: 3;
`;

var ContentInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
`;

var IconCircle = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin: 0 auto 16px auto;
  background: ${function (props) {
    return props.$focused
      ? "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.02) 100%)";
  }};
  border: ${function (props) {
    return props.$focused
      ? "2px solid rgba(255,255,255,0.5)"
      : "1px solid rgba(255,255,255,0.2)";
  }};
`;

var IconInner = styled.div`
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
`;

var Label = styled.div`
  color: ${function (props) { return props.$focused ? "#FFFFFF" : "rgba(255, 255, 255, 0.85)"; }};
  font-size: 18px;
  font-weight: 700;
  font-family: 'AnjomanMax', sans-serif;
  letter-spacing: 0.5px;
`;

var InnerBorder = styled.div`
  position: absolute;
  top: 1px;
  left: 1px;
  right: 1px;
  bottom: 1px;
  border-radius: 14px;
  border: ${function (props) {
    return props.$focused
      ? FOCUS_BORDER.active
      : '1px solid rgba(255, 255, 255, 0.08)';
  }};
  pointer-events: none;
  z-index: 2;
`;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

var ViewMoreCard = function (props) {
  var onFocus = props.onFocus;
  var actionLink = props.actionLink;
  var onMoreTap = props.onMoreTap;
  var width = props.width || 250;
  var height = props.height || 350;
  var shapeType = props.shapeType || "movie";
  var contentKind = props.contentKind;
  var focusKey = props.focusKey;

  var navigate = useNavigate();

  // Parse action link format: "type;target" or "type;target;extra"
  var parseActionLink = function (link) {
    if (!link) return null;
    var parts = link.split(";");
    if (parts.length < 2) return null;

    var type = parts[0];
    var target = parts[1];

    if (!type || !target) return null;

    switch (type) {
      case "genre":
        return "/genre/" + target;
      case "category":
        return "/category/" + target;
      case "artist":
      case "artists":
        return "/artists/" + target;
      case "movie":
      case "series":
      case "program":
      case "music":
      case "animation":
        return "/" + type + "/" + target;
      default:
        return null;
    }
  };

  var handleEnterPress = function () {
    if (onMoreTap) {
      onMoreTap();
    } else {
      var route = parseActionLink(actionLink);
      if (route) {
        // Append content type filter so the view more page locks to this kind
        var finalRoute = contentKind ? route + "?kind=" + contentKind : route;
        navigate(finalRoute);
      }
    }
  };

  var focusableResult = useFocusable({
    onFocus: onFocus,
    onEnterPress: handleEnterPress,
    focusKey: focusKey || "view_more_card",
  });

  var focused = focusableResult.focused;
  var ref = focusableResult.ref;
  var focusSelf = focusableResult.focusSelf;

  var inputModeResult = useInputMode();
  var setMouseFocus = inputModeResult.setMouseFocus;
  var handleMouseEnter = useCallback(function () {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Dimensions matching content card aspect ratios
  var getDimensions = function () {
    switch (shapeType) {
      case "music":
      case "animation":
        return { width: 250, height: 250 };
      case "artist":
        return { width: 215, height: 215 };
      case "program":
      case "sport":
        return { width: 620, height: 350 };
      case "movie":
      case "series":
      case "searchbox":
      default:
        return { width: width, height: height };
    }
  };

  var dimensions = getDimensions();
  var cardWidth = dimensions.width;
  var cardHeight = dimensions.height;

  return (
    <CardWrapper
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onClick={handleEnterPress}
      $focused={focused}
      style={{
        width: cardWidth + "px",
        minWidth: cardWidth + "px",
        height: cardHeight + "px",
        minHeight: cardHeight + "px",
      }}
    >
      {/* Top gradient shine layer - glassmorphism effect without blur */}
      <TopShine />

      {/* Side gradient accent */}
      <SideAccent />

      {/* Bottom gradient shadow layer */}
      <BottomShadow />

      {/* Content - centered arrow and text */}
      <ContentContainer>
        <ContentInner>
          {/* Circular icon container with gradient border */}
          <IconCircle $focused={focused}>
            <IconInner>
              <FaArrowLeft
                style={{
                  color: focused ? "#FFFFFF" : "rgba(255, 255, 255, 0.8)",
                  fontSize: "28px",
                }}
              />
            </IconInner>
          </IconCircle>
          <Label $focused={focused}>مشاهده همه</Label>
        </ContentInner>
      </ContentContainer>

      {/* Subtle inner border glow for depth */}
      <InnerBorder
        $focused={focused} />
    </CardWrapper>
  );
};

export default ViewMoreCard;
